/**
 * UI Complete Config Contract & Invariants
 * Authority: UI Config Contract Normalization (System Law)
 *
 * DIRECT MODULE EXPORTS (Phase 9d):
 * This module is the single source of truth for all UI invariants.
 * No runtime global injection — all values defined here, imported via ES modules.
 */

// ============================================================
// VISUAL CONSTANTS (Law: VISUAL_UNIFORMITY)
// ============================================================

// DEPRECATED — DO NOT IMPORT. Will be removed in next epoch bump. Use DESIGN_INTENTS instead.
export const UI_COLORS = {
    // DEPRECATED: Use DESIGN_INTENTS.primary instead.
    PRIMARY_ACTION: '#191970',  // DEPRECATED: Aligned to Brand navy per DESIGN_LANGUAGE_SKILL.md
    SECONDARY_ACTION: 'neutral',
    SUCCESS: '#16A34A',
    WARNING: '#D97706',
    ERROR: '#DC2626',
    INFO: '#3B82F6',

    // Remediation Additions (Visual Law Enforcement)
    OVERLAY: 'rgba(0, 0, 0, 0.1)',
    PANEL_BG: '#F8F9FA', // DESIGN_LANGUAGE_SKILL.md — Surface grey
    MUTED_BG: '#F0F0F8', // DESIGN_LANGUAGE_SKILL.md — Brand navy light

    SUCCESS_BG: '#DCFCE7',
    SUCCESS_TEXT: '#16A34A', // DESIGN_LANGUAGE_SKILL.md — Verdict green
    ERROR_BG: '#FEE2E2',
    ERROR_TEXT: '#DC2626', // DESIGN_LANGUAGE_SKILL.md — Verdict red

    SECONDARY_TEXT: '#6B7280', // DESIGN_LANGUAGE_SKILL.md — Text secondary
};

// ============================================================
// DESIGN INTENTS (V3) — replaces UI_COLORS as the canonical
// color authority. All values are Tailwind class tokens; zero
// hardcoded hex values. Law: VISUAL_UNIFORMITY + PKG-008.
// ============================================================

export const DESIGN_INTENTS = {
    primary:  { bg: 'bg-brand-900', text: 'text-white', border: 'border-brand-700', hover: 'hover:bg-brand-dark' }, // Single Authority: matches INTENT_TO_BUTTON.primary.hover in task-display-config.js
    positive: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', hover: 'hover:bg-emerald-100' },
    negative: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', hover: 'hover:bg-red-100' },
    warning:  { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', hover: 'hover:bg-amber-100' },
    info:     { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', hover: 'hover:bg-blue-100' },
    neutral:  { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200', hover: 'hover:bg-slate-100' },
    muted:    { bg: 'bg-slate-50', text: 'text-slate-400', border: 'border-slate-100', hover: 'hover:bg-slate-100' },
};

// V7 recommendation-to-intent mapping (Phase 9i)
// V7 line 17: save→positive, skip→negative, review→cautious(=warning)
export const REC_TO_INTENT = {
    // Sector Intelligence recommendations
    save: 'positive',
    review: 'warning',
    skip: 'negative',
    // FCA recommendations (Deal Evaluator)
    lean_in: 'positive',
    hold: 'warning',
    pass: 'negative',
    insufficient_data: 'neutral',
};

// V7 solid accent colors per intent — Tailwind classes for left accent bars + group dots
export const INTENT_SOLID_CLASSES = {
    positive: 'bg-emerald-500',
    warning: 'bg-amber-500',
    negative: 'bg-red-500',
    primary: 'bg-brand-900',
    info: 'bg-blue-500',
    neutral: 'bg-slate-400',
    muted: 'bg-slate-300',
};

// V7 card background tint per intent — Tailwind classes
export const INTENT_TINT_CLASSES = {
    positive: 'bg-emerald-50',
    warning: 'bg-amber-50',
    negative: 'bg-red-50',
    primary: 'bg-brand-50',
    info: 'bg-blue-50',
    neutral: 'bg-slate-50',
    muted: 'bg-slate-50',
};

// ============================================================
// SEMANTIC COLOR CONTRACT (Law: MEANING_TO_COLOR)
// Every color assignment in the app must reference a meaning,
// not a raw color. Components import from here.
// V3: Intent-based mapping — all leaf values spread from
// DESIGN_INTENTS so the intent layer is the single source.
// ============================================================

export const SEMANTIC_COLORS = {
    // V3: Intent-based mapping (belief tags, confidence, outcomes)
    confidence: {
        high:    { ...DESIGN_INTENTS.positive, label: 'Strong Signal' },
        medium:  { ...DESIGN_INTENTS.warning, label: 'Moderate Signal' },
        low:     { ...DESIGN_INTENTS.negative, label: 'Weak Signal' },
    },
    provenance: {
        existing:  DESIGN_INTENTS.positive,
        contrary:  DESIGN_INTENTS.negative,
        novel:     DESIGN_INTENTS.info,
        adjacent:  DESIGN_INTENTS.warning,
    },
    outcome: {
        saved:      DESIGN_INTENTS.positive,
        rejected:   DESIGN_INTENTS.negative,
        deferred:   DESIGN_INTENTS.warning,
        overridden: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', hover: 'hover:bg-purple-100' },
    },
    status: {
        success: DESIGN_INTENTS.positive,
        warning: DESIGN_INTENTS.warning,
        error:   DESIGN_INTENTS.negative,
        info:    DESIGN_INTENTS.info,
        neutral: DESIGN_INTENTS.neutral,
    },
};

// DESIGN_LANGUAGE_SKILL.md — Section 4: Spacing System
export const UI_DIMENSIONS = {
    CARD_MIN_HEIGHT: '200px',
    BUTTON_HEIGHT: 'h-11', // 44px min touch target
    BORDER_RADIUS: 'rounded-lg', // 8px cards
    SPACING_UNIT: '1rem',
};

// DESIGN_LANGUAGE_SKILL.md — Section 3: Typography (Inter only, 4 sizes, max weight 600)
export const UI_TYPOGRAPHY = {
    CARD_TITLE: 'text-[22px] font-semibold text-gray-900', // Verdict/page title size
    SECTION_LABEL: 'text-sm font-semibold uppercase tracking-[0.08em] text-gray-900',
    BODY_TEXT: 'text-base text-gray-900', // 16px body
    CAPTION: 'text-xs text-gray-500', // 12px meta

    // Remediation Additions — updated to match design language
    DETAIL_TITLE: 'text-[22px] font-semibold text-gray-900',
    STAT_NUMBER: 'text-[22px] font-semibold text-gray-900',
};

export const UI_TIMINGS = {
    SPLASH_DURATION_MS: 4000,  // Boot gate max duration (Phase 9a.2: extended for premium entrance)
    TRANSITION_DURATION_MS: 300,  // Standard animations
    DEBOUNCE_MS: 150,  // Input debouncing
};

// ============================================================
// WELCOME TIMING (Phase 9a.2: Premium Entrance Sequence)
// Controls the SystemWelcome animation choreography.
// All values are milliseconds. Law: VISUAL_UNIFORMITY.
// ============================================================

export const WELCOME_TIMING = {
    logoDeblur: 700,      // ms — logo materialization (scale 0.88→1.0, blur 12→0)
    logoSettled: 200,     // ms — hold at full clarity
    taglineFadeIn: 600,   // ms — tagline entrance
    holdDuration: 1500,   // ms — premium stillness
    exitDuration: 1000,   // ms — curtain lift exit
};

// ============================================================
// WELCOME SIZES (Phase 9j: Scaled Welcome Experience)
// Controls logo, brand text, and tagline sizes on the welcome screen.
// All values in px. Law: VISUAL_UNIFORMITY.
// ============================================================

export const WELCOME_SIZES = {
    logoHeight: 280,       // px — 5x original 56px; logo-large.png rendered at this height
    logoWidth: 280,        // px — square logo asset, matches height
    brandTextSize: 76,     // px — proportional scale from original ~36px
    taglineSize: 30,       // px — proportional scale from original ~18px
};

// ============================================================
// DEAL SCREEN CARD (DS-CARD-V4) — exact measurements from deal-screen-v3.jsx
// prototype + live DOM cross-reference. Authority: this block.
// DealScreenInboxCard.jsx and DealScreenDetailView.jsx read ONLY from here.
// ============================================================

// DESIGN_LANGUAGE_SKILL.md — Section 2, 3, 4, 5: Card tokens aligned
export const DEAL_SCREEN_CARD = {
  // Card shell (inbox card)
  shell: {
    background:    '#FFFFFF',          // White
    border:        '#E5E7EB',          // Border grey
    borderRadius:  '8px',             // Section 5: cards 8px
    paddingTop:    '24px',
    paddingBottom: '24px',
    paddingLeft:   '24px',
    paddingRight:  '24px',            // Section 4: card padding 24px
  },

  // Left accent bar (4px, driven by call decision)
  accentBar: {
    width:           '4px',
    worthACall:      '#191970',        // Brand navy
    notWorthACall:   '#D97706',        // Verdict amber
  },

  // Company name
  companyName: {
    fontSize:       '22px',            // Section 3: verdict/page title
    fontWeight:     '600',             // Semibold max
    color:          '#111827',         // Text primary
    letterSpacing:  '0',
    lineHeight:     '1.3',
  },

  // Stage / sector sub-label
  stageSector: {
    fontSize:       '14px',            // Section 3: section heading
    fontWeight:     '600',
    color:          '#6B7280',         // Text secondary
    letterSpacing:  '0.08em',
    textTransform:  'uppercase',
  },

  // Reason paragraph
  reason: {
    fontSize:       '16px',            // Section 3: body
    fontWeight:     '400',
    color:          '#111827',         // Text primary
    lineHeight:     '1.6',
  },

  // Verdict row divider
  verdictDivider: {
    color:          '#E5E7EB',         // Border grey
    thickness:      '1px',
    marginTop:      '12px',
    marginBottom:   '10px',
  },

  // Verdict row label (Problem / Solution / Market Signal)
  verdictLabel: {
    fontSize:       '14px',            // Section 3: section heading
    fontWeight:     '600',
    color:          '#6B7280',         // Text secondary
    textTransform:  'uppercase',
    letterSpacing:  '0.08em',
  },
};

// Verdict pill — exact from prototype + live DOM.
// Three states. Applied to inbox card pills AND detail view section header badges.
// DESIGN_LANGUAGE_SKILL.md — Section 2, 5: Verdict pill tokens
export const VERDICT_PILL = {
  strong: {
    background: '#ECFDF5',
    text:       '#16A34A',            // Verdict green
    border:     '#6EE7B7',
    dot:        '#16A34A',            // Verdict green
    dotSize:    '6px',
    label:      'Strong',
  },
  moderate: {
    background: '#FFFBEB',
    text:       '#D97706',            // Verdict amber
    border:     '#FCD34D',
    dot:        '#D97706',
    dotSize:    '6px',
    label:      'Moderate',
  },
  weak: {
    background: '#FFFBEB',
    text:       '#D97706',            // Verdict amber (unified)
    border:     '#FDE68A',
    dot:        '#D97706',
    dotSize:    '6px',
    label:      'Weak',
  },
  unclear: {
    background: '#F8F9FA',            // Surface grey
    text:       '#6B7280',            // Text secondary
    border:     '#E5E7EB',            // Border grey
    dot:        '#6B7280',
    dotSize:    '6px',
    label:      'Unclear',
  },
  // Shared pill shape — Section 5: pills 999px radius
  pill: {
    borderRadius:  '9999px',
    borderWidth:   '1px',
    fontSize:      '12px',            // Section 3: meta size
    fontWeight:    '600',             // Max weight 600
    paddingTop:    '3px',
    paddingBottom: '3px',
    paddingLeft:   '8px',
    paddingRight:  '8px',
  },
};

// Call badge — top-right of inbox card and detail header. Two states.
// DESIGN_LANGUAGE_SKILL.md — Section 2, 5: Call badge tokens
export const CALL_BADGE = {
  worthACall: {
    background: '#191970',            // Brand navy
    text:       '#FFFFFF',
    border:     '#191970',
    dot:        '#FFFFFF',
    dotSize:    '6px',
    dotAnimation: 'pulse',
  },
  notWorthACall: {
    background: '#FFFBEB',
    text:       '#D97706',            // Verdict amber
    border:     '#FDE68A',
    dot:        '#D97706',
    dotSize:    '6px',
    dotAnimation: 'none',
  },
  // Shared badge shape — Section 5: pills 999px radius
  badge: {
    borderRadius:  '9999px',
    borderWidth:   '1px',             // Section 5: 1px border
    fontSize:      '12px',
    fontWeight:    '600',             // Max weight 600
    paddingTop:    '6px',
    paddingBottom: '6px',
    paddingLeft:   '16px',
    paddingRight:  '16px',
  },
};

// Detail view tokens — every element in the detail panel
// DESIGN_LANGUAGE_SKILL.md — Section 2, 3, 4, 5: Detail view tokens
export const DS_DETAIL_VIEW = {
  // Content wrapper — Section 4: max-width 780px
  wrapper: { padding: '24px', maxWidth: '780px' },

  // Header card — Section 5: 8px radius, single shadow
  headerCard: {
    background:    '#FFFFFF',
    border:        '#E5E7EB',          // Border grey
    borderRadius:  '8px',
    paddingTop:    '24px',
    paddingBottom: '24px',
    paddingLeft:   '24px',
    paddingRight:  '24px',
    marginBottom:  '16px',             // Section 4: gap between cards
    boxShadow:     '0 1px 3px rgba(0,0,0,0.08)',
  },

  // Company name in detail header — Section 3: verdict/page title
  companyName: {
    fontSize:      '22px',
    fontWeight:    '600',
    color:         '#111827',          // Text primary
    letterSpacing: '0',
    lineHeight:    '1.3',
  },

  // Stage / sector in detail header — Section 3: section heading
  stageSector: {
    fontSize:      '14px',
    fontWeight:    '600',
    color:         '#6B7280',          // Text secondary
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },

  // KPI tile accent colors by verdict/signal
  kpiAccent: {
    strong:   '#16A34A',              // Verdict green
    moderate: '#D97706',              // Verdict amber
    unclear:  '#6B7280',              // Text secondary
    weak:     '#6B7280',
    neutral:  '#E5E7EB',              // Border grey
  },

  // KPI value text color
  kpiValueColor: '#111827',            // Text primary

  // Decision rationale strip
  rationaleStrip: {
    worthACall: {
      background:  '#F0F0F8',         // Brand navy light
      border:      '#E5E7EB',
      borderLeft:  '#191970',          // Brand navy
    },
    notWorthACall: {
      background:  '#F8F9FA',         // Surface grey
      border:      '#E5E7EB',
      borderLeft:  '#6B7280',          // Text secondary
    },
  },

  // Section panel left accent by verdict
  sectionAccent: {
    strong:   '#16A34A',               // Verdict green
    moderate: '#D97706',
    weak:     '#D97706',
    unclear:  '#6B7280',
  },

  // Section title — Section 3: section heading
  sectionTitle: {
    fontSize:   '14px',
    fontWeight: '600',
    color:      '#111827',
  },

  // Section body text — Section 3: body
  sectionBody: {
    fontSize:   '16px',
    fontWeight: '400',
    color:      '#111827',
    lineHeight: '1.6',
  },

  // Archive button
  archiveButton: {
    color:  '#6B7280',                 // Text secondary
    border: '#E5E7EB',                 // Border grey
  },

  // Back button
  backButton: {
    color:      '#9CA3AF',             // Text muted
    colorHover: '#191970',             // Brand navy
  },
};

// ============================================================
// DS-CARD-V5: Typography tokens for Deal Screen components
// Authority: this block. Both DealScreen components read from here.
// ============================================================

// DESIGN_LANGUAGE_SKILL.md — Section 2: Typography (Two Fonts, One Logic)
// DM Sans for structural tokens (displayXL, sectionTitle, sectionLabel, actionBtn).
// Inter for body/reading tokens. Authority: DESIGN_LANGUAGE_SKILL.md Section 2.
export const DS_TYPOGRAPHY = {
  // DESIGN_LANGUAGE_SKILL.md Section 2: DM Sans for page titles (structural)
  displayXL:      { fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: '22px', fontWeight: 600, lineHeight: 1.3 },
  cardTitle:      { fontFamily: 'Inter, system-ui, sans-serif', fontSize: '22px', fontWeight: 600, lineHeight: 1.3 },
  // DESIGN_LANGUAGE_SKILL.md Section 2: DM Sans for section LABELS (structural) -- 14px uppercase small caps.
  // WARNING: sectionTitle is for structural labels ONLY (e.g. PROBLEM, PENDING, MARKET SIGNAL).
  // It is NOT for card titles, article titles, or any human-written text.
  // Those use listItemTitle (18px, mixed case, no uppercase transform).
  // This is the single most common token misuse in this codebase.
  // Before applying sectionTitle to any element, ask: is this a short
  // structural label, or a title a human wrote? If human-written, use listItemTitle.
  sectionTitle:   { fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: '14px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: 1.4 },
  kpiValue:       { fontFamily: 'Inter, system-ui, sans-serif', fontSize: '22px', fontWeight: 600, lineHeight: 1.3 },
  scoreDisplay:   { fontFamily: 'Inter, system-ui, sans-serif', fontSize: '22px', fontWeight: 600, lineHeight: 1.3 },
  statusText:     { fontFamily: 'Inter, system-ui, sans-serif', fontSize: '22px', fontWeight: 600 },
  bodyMd:         { fontFamily: 'Inter, system-ui, sans-serif', fontSize: '16px', fontWeight: 400, lineHeight: 1.6 },
  bodySm:         { fontFamily: 'Inter, system-ui, sans-serif', fontSize: '16px', fontWeight: 400, lineHeight: 1.6 },
  cardReason:     { fontFamily: 'Inter, system-ui, sans-serif', fontSize: '16px', fontWeight: 400, lineHeight: 1.6 },
  // DESIGN_LANGUAGE_SKILL.md Section 2: DM Sans for button labels (structural)
  actionBtn:      { fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: '14px', fontWeight: 600 },
  // DESIGN_LANGUAGE_SKILL.md Section 2: DM Sans for knob selector labels (structural)
  selectorBtn:    { fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: '12px', fontWeight: 600 },
  // FCA_FRONTEND_SPEC §2.1: DM Sans 18px 600 for startup list item names
  listItemTitle:  { fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: '18px', fontWeight: 600, lineHeight: 1.3 },
  // FCA_FRONTEND_SPEC §2.1: DM Sans 12px 700 uppercase for inline badges
  badgeText:      { fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' },
  // FCA_FRONTEND_SPEC §4: DM Sans 14px 600 for drop zone label
  dropZoneLabel:  { fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: '14px', fontWeight: 600 },
  backLink:       { fontFamily: 'Inter, system-ui, sans-serif', fontSize: '12px', fontWeight: 400, lineHeight: 1.4 },
  loadingText:    { fontFamily: 'Inter, system-ui, sans-serif', fontSize: '16px', fontWeight: 400 },
  // DESIGN_LANGUAGE_SKILL.md Section 2: DM Sans for section labels (structural)
  sectionLabel:   { fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: '14px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: 1.4 },
  sectionSubLabel:{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '12px', fontWeight: 400 },
  // DESIGN_IMPLEMENTATION_CONTRACT.md Section 2.3: hybrid direction page title (DM Sans 28/800, tight tracking).
  pageTitle:      { fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: '28px', fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.025em' },
  // DESIGN_IMPLEMENTATION_CONTRACT.md Section 2.3: tighter 11px structural label for seven-tile summary + sidebar group headings.
  microLabel:     { fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', lineHeight: 1.0 },
  // Aliases preserved for backward compat during migration — point to new tokens
  get monoLabel()  { return this.sectionLabel; },
  get monoSub()    { return this.sectionSubLabel; },
  verdictPillText: { fontSize: '12px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' },
};

// ============================================================
// DS-CARD-V5: Semantic colors for Deal Screen components
// ============================================================

// DESIGN_LANGUAGE_SKILL.md — Section 3: Colour (Three Conversations That Never Overlap)
// ai.action = Violet #7C3AED — AI is acting. ai.actionDark = hover state.
// Authority: DESIGN_LANGUAGE_SKILL.md Section 3.
export const DS_COLORS = {
  text: {
    primary:      '#111827',    // Text primary
    secondary:    '#6B7280',    // Text secondary
    muted:        '#9CA3AF',    // Text muted
    body:         '#111827',    // Same as primary per design language
    error:        '#DC2626',    // Verdict red / danger
    errorStrong:  '#DC2626',    // Verdict red / danger
    brand:        '#191970',    // Brand navy
    success:      '#16A34A',    // Verdict green
  },
  border: {
    default: '#E5E7EB',         // Border grey
    subtle:  '#E5E7EB',         // Border grey (unified)
    brand:   '#191970',         // Brand navy
  },
  surface: {
    white: '#FFFFFF',           // White
    warm:  '#F0F0F8',           // Brand navy light
    slate: '#F8F9FA',           // Surface grey
  },
  selection: {
    border:      '#191970',     // Brand navy
    borderWidth: '2px',
    shadow:      '0 1px 3px rgba(0,0,0,0.08)',
  },
  // DESIGN_LANGUAGE_SKILL.md — Section 5: shadow on cards ONLY
  shadow: {
    card:      '0 1px 3px rgba(0,0,0,0.08)',
    section:   'none',  // Section 5: no shadow outside cards
    header:    'none',
    tooltip:   '0 4px 12px rgba(0,0,0,0.18)',
    actionBar: '0 -4px 12px rgba(10,15,46,0.06)',
  },
  overlay: {
    modal: 'rgba(0, 0, 0, 0.4)',
  },
  error: {
    background: '#FEE2E2',
    border:     '1px solid #DC2626',
    color:      '#DC2626',
  },
  // CEO directive (WI-INBOX-ROUTING-001): cockpit tooltips use solid black
  // background with white text for maximum contrast at the CEO's reading distance.
  tooltip: {
    background: '#111827',             // Neutral black (same as text.primary)
    foreground: '#FFFFFF',             // White
    border:     '1px solid #111827',
  },
  // DESIGN_LANGUAGE_SKILL.md Section 3: Violet = AI is acting. Never use for anything else.
  ai: {
    action:     '#7C3AED',             // Action AI — AI action buttons only
    actionDark: '#6D28D9',             // Action AI dark — hover state for AI buttons only
  },
  // WI-UI-A11Y-HOVER-FADE-AND-TINY-TEXT-2026-04-23: signal strength colours.
  // moderate = existing verdict amber #D97706 (amber-600 — established caution colour).
  // weak     = #F59E0B (amber-400 — lighter, clearly distinct from moderate at a glance,
  //            still unambiguously in the caution/amber family, no collision with
  //            green/red/navy/violet conversations).
  // Cross-check: #F59E0B is NOT used anywhere else in DS_COLORS or brand_registry.json.
  // Documented in DESIGN_LANGUAGE_SKILL.md Section 3 colour token table.
  signal: {
    moderate: '#D97706',  // Verdict amber (amber-600) — caution, directional
    weak:     '#F59E0B',  // Lighter amber (amber-400) — weak signal, below threshold
  },
  // WI-PROGRESS-CARD-V3: gradient accents for the connector fill in PipelineProgressCard.
  // Start = brand navy. End = lighter navy for the left-to-right progress fill.
  gradient: {
    brandStart: '#191970',             // Same hex as text.brand — flow start
    brandEnd:   '#3b3bc2',             // Brand navy's lighter sibling — flow end
  },
};

// ============================================================
// SYSTEM BOOT ERROR TOKENS (PKG-014 UIContractGuard)
// ============================================================

export const SYSTEM_BOOT_ERRORS = {
  codes: {
    BUILD_TRUTH_MISSING:      'boot_build_truth_missing',
    BACKEND_UNREACHABLE:      'boot_backend_unreachable',
    BACKEND_INVALID_IDENTITY: 'boot_backend_invalid_identity',
    FRONTEND_INVALID:         'boot_frontend_invalid',
  },
  messages: {
    boot_build_truth_missing:      'System could not verify build integrity — please refresh or contact support',
    boot_backend_unreachable:      'System is starting up — please wait a moment and refresh',
    boot_backend_invalid_identity: 'System returned an unexpected response — please refresh or contact support',
    boot_frontend_invalid:         'Application build could not be verified — please clear cache and refresh',
  },
  lockScreen: {
    padding:      '24px',
    fontFamily:   "'Inter', system-ui, sans-serif",
    background:   '#FEE2E2',
    border:       '1px solid #DC2626',  // Danger — intentional for boot error state
    borderRadius: '8px',                // Section 5: cards 8px
    maxWidth:     '560px',
    margin:       '80px auto',
  },
  title: {
    fontSize:   '22px',                 // Section 3: verdict size
    fontWeight:  600,                   // Max weight 600
    color:      '#DC2626',              // Danger
    margin:     '0 0 12px',
  },
  body: {
    fontSize:   '16px',                 // Section 3: body
    lineHeight: '1.6',
    color:      '#111827',              // Text primary
  },
  footer: {
    fontSize:   '12px',                 // Section 3: meta
    color:      '#9CA3AF',              // Text muted
    marginTop:  '24px',
  },
};

// ============================================================
// DS-CARD-V5: KPI tile tokens
// ============================================================

// DESIGN_LANGUAGE_SKILL.md — Section 2, 5: KPI tile tokens
export const KPI_TILE = {
  shell: {
    background:   '#FFFFFF',
    border:       '1px solid #E5E7EB', // Border grey
    borderRadius: '8px',               // Section 5: cards 8px
    padding:      '12px 14px',
  },
  accentBar: {
    height:       '3px',
    borderRadius: '8px 8px 0 0',
    byVerdict: {
      strong:   '#16A34A',             // Verdict green
      moderate: '#D97706',
      weak:     '#D97706',
      unclear:  '#6B7280',             // Text secondary
    },
  },
  tileBg: {
    strong:   '#FFFFFF',
    moderate: '#FFFFFF',
    weak:     '#FFFFFF',
    unclear:  '#FFFFFF',
    default:  '#FFFFFF',
  },
  tooltipsEnabled: false,
  tooltips: {
    overall:      'Composite signal across all three dimensions.',
    problem:      'Is the pain real, buyer-specific, and acute enough to trigger spend?',
    solution:     'Does the product solve that specific pain, and is there a defensibility mechanism?',
    market_signal:'Is the market real at Indian economics, and is the competitive position defensible?',
    verdicts: {
      strong:  'Strong — sufficient evidence, hypothesis holds under scrutiny.',
      // RT-9: MODERATE retired as a band word (WI-R) — tooltip copy no longer surfaces it to the reader.
      moderate:'Worth a second look — directionally positive, but a material gap limits conviction.',
      weak:    'Weak — cautious view. Evidence exists but is insufficient or contradicted.',
      unclear: 'Unclear — deck provides no evaluable signal on this dimension.',
    },
  },
  valueColor: {
    strong:   '#16A34A',               // Verdict green
    moderate: '#D97706',
    weak:     '#D97706',
    unclear:  '#6B7280',               // Text secondary
    default:  '#111827',               // Text primary
  },
  // Fixed evaluation questions per section — same for every startup.
  // Authority: docs/agents/pitch-deck-review/DEAL_SCREEN_SKILL_DOC.md → Evaluation Questions Per Section
  // Q1 is the primary question shown as sub line 1 on the KPI tile.
  // Q2 is shown as sub line 2 (if space permits) or in the tooltip.
  sectionQuestions: {
    problem: {
      q1: 'Is the pain structural and urgent?',
      q2: 'Is the trigger strong enough to act on?',
    },
    solution: {
      q1: 'Is this the strongest answer to the pain?',
      q2: 'Does it stand out from what already exists?',
    },
    market_signal: {
      q1: 'Is the India market large enough to matter?',
      q2: 'Is there demand evidence at the right price?',
    },
  },
};

// ============================================================
// DS-CARD-V5: Section panel tokens
// ============================================================

// DESIGN_LANGUAGE_SKILL.md — Section 2, 4, 5: Section panel tokens
export const SECTION_PANEL = {
  shell: {
    background:   '#FFFFFF',
    borderOther:  '1px solid #E5E7EB',  // Border grey
    borderRadius: '8px',                // Section 5: cards 8px
    boxShadow:    '0 1px 3px rgba(0,0,0,0.08)',
  },
  headerRow: {
    padding:      '16px 24px',
    borderBottom: '1px solid #E5E7EB',
    background:   '#FFFFFF',
  },
  bodyPadding: '24px',                  // Section 4: card padding 24px
  bodyWash: {
    strong:   '#FFFFFF',
    moderate: '#FFFFFF',
    weak:     '#FFFFFF',
    unclear:  '#FFFFFF',
  },
  leftBorderByVerdict: {
    strong:   '#16A34A',                // Verdict green
    moderate: '#D97706',
    weak:     '#D97706',
    unclear:  '#6B7280',                // Text secondary
  },
  adjustFooter: {
    background: '#F0F0F8',              // Brand navy light
    borderTop:  '1px solid #E5E7EB',
    padding:    '16px 24px 20px',
  },
  versionBadge: {
    color:        '#191970',            // Brand navy
    background:   '#F0F0F8',            // Brand navy light
    border:       '1px solid #E5E7EB',
    borderRadius: '999px',
    padding:      '2px 9px',
  },
  keyFinding: {
    background:   '#FFFFFF',
    border:       '1px solid #E5E7EB',
    labelColor:   '#D97706',            // Verdict amber
    borderRadius: '8px',
    padding:      '12px',
  },
  historyCard: {
    background:   '#F0F0F8',            // Brand navy light
    border:       '1px solid #E5E7EB',
    borderRadius: '8px',
    padding:      '12px',
  },
};

// ============================================================
// DS-CARD-V5: Decision rationale panel tokens
// ============================================================

// DESIGN_LANGUAGE_SKILL.md — Section 2, 5: Rationale panel tokens
export const DECISION_RATIONALE = {
  shell: {
    background:   '#F8F9FA',           // Surface grey
    borderLeft:   '4px solid #6B7280', // Text secondary
    borderOther:  '1px solid #E5E7EB', // Border grey
    borderRadius: '8px',
    padding:      '14px 20px',
  },
};

// ============================================================
// DS-CARD-V5: Rationale block tokens (per-section Q&A answers)
// ============================================================

// DESIGN_LANGUAGE_SKILL.md — Section 2, 5: Rationale block tokens
export const RATIONALE_BLOCK = {
  shell: {
    borderRadius: '8px',
    border: '1px solid',
    padding: '16px 20px',
    marginBottom: '8px',
  },
  byVerdict: {
    strong:   { background: '#FFFFFF', border: '#6EE7B7' },
    moderate: { background: '#FFFFFF', border: '#FCD34D' },
    weak:     { background: '#FFFFFF', border: '#FDE68A' },
    unclear:  { background: '#FFFFFF', border: '#E5E7EB' },  // Border grey
  },
  overall: {
    background: '#FFFFFF',
    border: '#E5E7EB',                 // Border grey
  },
  sectionLabel: {
    // inherits DS_TYPOGRAPHY.sectionLabel
    marginBottom: '6px',
  },
  answerLine1: {
    // inherits DS_TYPOGRAPHY.bodySm
    color: '#111827',                  // Text primary
    marginBottom: '4px',
  },
  answerLine2: {
    // inherits DS_TYPOGRAPHY.bodySm
    color: '#6B7280',                  // Text secondary
  },
};

// ============================================================
// DS-CARD-V5: Action button tokens
// ============================================================

// DESIGN_LANGUAGE_SKILL.md — Section 3 (ai colour) + Section 2 (DM Sans button labels)
// ai entry: Violet fill, white text, hover → actionDark. Authority: DESIGN_LANGUAGE_SKILL.md Section 3.
export const ACTION_BUTTONS = {
  rerun: {
    background:   '#FFFFFF',
    color:        '#191970',           // Brand navy
    border:       '1px solid #E5E7EB', // Border grey, 1px per Section 5
    borderRadius: '6px',               // Section 5: buttons 6px
    minHeight:    '44px',
  },
  selectorDefault: {
    background:   '#F8F9FA',           // Surface grey
    color:        '#6B7280',           // Text secondary
    border:       '1px solid #E5E7EB',
    borderRadius: '6px',
    minHeight:    '44px',
  },
  archive: {
    background:   '#FFFFFF',
    color:        '#6B7280',           // Text secondary
    border:       '1px solid #E5E7EB',
    borderRadius: '6px',
    minHeight:    '44px',
  },
  knobStates: {
    default: {
      bg: DS_COLORS.surface.slate,
      text: DS_COLORS.text.secondary,
      border: DS_COLORS.border.default,
    },
    active: {
      bg: DS_COLORS.surface.warm,
      text: DS_COLORS.text.brand,
      border: DS_COLORS.border.brand,
    },
  },
  // DESIGN_LANGUAGE_SKILL.md Section 3: Violet fill = AI is acting. Never use for non-AI actions.
  ai: {
    background:      '#7C3AED',        // Action AI — DS_COLORS.ai.action
    color:           '#FFFFFF',
    border:          '1.5px solid #7C3AED',
    borderRadius:    '6px',            // Section 4: button shape
    minHeight:       '44px',           // Section 4: min-height accessibility
    hoverBackground: '#6D28D9',        // Action AI dark — DS_COLORS.ai.actionDark
  },
  // Universal hover tokens
  hover: {
    border:              '#191970',    // Brand navy
    text:                '#191970',
    background:          '#FFFFFF',
    primaryBackground:   '#13134F',    // Brand navy hover — per brand_registry.json
  },
};

// ============================================================
// DESIGN_LANGUAGE_SKILL.md — Section 4: Spacing System
// ============================================================

export const DS_SPACING = {
  base:              4,       // px — base unit
  cardPadding:       24,      // px — card internal padding
  cardGap:           16,      // px — gap between cards
  labelToContent:    12,      // px — gap between section label and first content line
  buttonGap:         8,       // px — gap between action bar buttons
  minTouchTarget:    44,      // px — minimum height for interactive elements
  pageMaxWidth:      780,     // px — page content max-width, centered (shorter content)
  paragraphSpacing:  20,      // px — between paragraphs in analysis sections
  // DESIGN_IMPLEMENTATION_CONTRACT.md Section 2.2: hybrid direction sidebar + long-read widths.
  sidebarWidth:      240,     // px — legacy Atrium fixed width. Retained for non-cockpit consumers; new code uses sidebarWidthExpanded / sidebarWidthCollapsed.
  // WI-UI-CLEANUP-001 Stage 1 — narrow-default + hover-expand + pin behaviour.
  // Expanded matches the Cockpit mockup rail column (232px). Collapsed 64px
  // per WI §4.1/§4.2 and satisfies the 44px minTouchTarget for icon-only rows.
  sidebarWidthExpanded:  232,
  sidebarWidthCollapsed: 64,
  longReadMaxWidth:  860,     // px — Deal Evaluator FCA + Strategy Insights centred long-read.
};

// ============================================================
// DESIGN_LANGUAGE_SKILL.md — Section 5: Border and Shape
// ============================================================

export const DS_BORDERS = {
  width:             '1px',
  color:             '#E5E7EB',   // Border grey token
  radiusCard:        '8px',
  radiusPill:        '999px',     // Fully rounded
  radiusButton:      '6px',
  shadow:            '0 1px 3px rgba(0,0,0,0.08)', // Cards only
};

// ============================================================
// BAND_WORD_ALWAYS_VISIBLE — WI-DS-RENDER-NAME-001 invariant token
// Authority: DEAL_SCREEN_AGENT_SPEC.md §4a; DEAL_SCREEN_PROSE_STYLE.md §2;
//            CLAUDE_DESIGN_BRIEF_2026-04-18.md (accessibility)
//
// Every reader-facing band tile MUST show the band WORD (STRONG/WEAK — MODERATE retired by WI-R)
// prominently — colour alone never carries meaning. The word must be:
//   - font-size >= 16px (accessibility minimum)
//   - font-weight >= 700 (bold headings rule)
//   - strong contrast (dark text on light, or band-specific colour on white)
//   - touch target >= 44px (min tile height)
//   - border-width >= 2px thick on the band tile
// band_score (numeric) is internal-only: persisted but NEVER rendered to reader.
// ============================================================
export const BAND_WORD_ALWAYS_VISIBLE = {
  /** Minimum font size for the band word label. */
  minFontSizePx: 16,
  /** Minimum font weight for the band word label. */
  minFontWeight: 700,
  /** Minimum tile height to satisfy touch-target rule. */
  minTouchTargetPx: 44,
  /** Minimum border width on the band tile. */
  minBorderWidthPx: 2,
  /**
   * Reader-vocabulary map. Internal band enum → displayed word.
   * WI-C: the SINGLE SOURCE of the displayed band word is
   * ds-band-thresholds.js::internalBandToReaderLabel — components import THAT,
   * not this map. This copy is kept in sync (WEAK->WEAK, no kinder relabel; the
   * old WEAK->'MODERATE' here was a latent dual-store) so a future consumer that
   * reaches for it cannot silently reintroduce the relabel. Prefer the SSOT fn.
   * NO_SIGNAL is internal-only; the reader always sees WEAK.
   */
  readerLabel: {
    STRONG:    'STRONG',
    WEAK:      'WEAK',
    NO_SIGNAL: 'WEAK',
  },
  /** Colour map (token references to DS_COLORS). Component must import DS_COLORS. */
  bandColour: {
    STRONG:    '#16A34A',   // DS_COLORS.text.success — verdict green
    WEAK:      '#D97706',   // DS_COLORS.signal.moderate — amber caution
    NO_SIGNAL: '#6B7280',   // DS_COLORS.text.secondary — muted
  },
};

// ============================================================
// DS_ANIMATION — Semantic animation tokens for shared components
// Authority: WI-PROGRESS-CARD-POC. Zero inline animation values in component files.
// ============================================================

export const DS_ANIMATION = {
  pulseDuration:          1.4,          // seconds — legacy inner-dot pulse cycle
  pulseScale:             [1, 1.3, 1],  // Framer Motion scale keyframes — legacy inner dot
  stepTransitionDuration: 0.2,          // seconds — step state change transition
  // WI-PROGRESS-CARD-V3 — Stripe-inspired horizontal tracker animation tokens.
  ringPulseDuration:      2.0,          // seconds — breathing ring cycle around active step
  ringPulseScale:         [1, 1.15, 1], // Framer Motion scale keyframes — outward ring breath
  ringPulseOpacity:       0.15,         // baseline opacity of the 2px pulse ring
  checkmarkDrawDuration:  0.6,          // seconds — SVG pathLength draw for check + X
  checkmarkStaggerDelay:  0.2,          // seconds — left-to-right delay between cascaded checkmarks on full completion
  connectorFillDuration:  0.8,          // seconds — gradient connector fill transition
  checkmarkEase:          [0.65, 0, 0.35, 1], // cubic-bezier — shared ease for draw + fill
};

// ============================================================
// DS_MOTION — Hybrid direction motion vocabulary
// Authority: docs/contracts/DESIGN_IMPLEMENTATION_CONTRACT.md Section 4.
// Components import these tokens. Zero hardcoded durations / easings in
// component files. DS_ANIMATION stays dedicated to the Stripe-style
// pipeline tracker; DS_MOTION covers everything else (collapsible,
// hover, regeneration, navigation, loading, toast).
// ============================================================

export const DS_MOTION = {
  // Collapsible sections across FCA parameters, KB entries, Strategy
  // Insights panes. Grid-template-rows + opacity only — never height-based.
  collapsible: {
    durationMs: 240,
    ease: 'cubic-bezier(0.22, 1, 0.36, 1)',
  },
  // Hover states. shadowTranslateY applies to the shadow holder, not the
  // button itself, to avoid layout shift.
  hover: {
    primaryDuration: 200,
    primaryEase: 'cubic-bezier(0.4, 0, 0.2, 1)',
    ghostDuration: 150,
    ghostEase: 'cubic-bezier(0.4, 0, 0.2, 1)',
    rowDuration: 180,
    rowEase: 'cubic-bezier(0.4, 0, 0.2, 1)',
    shadowTranslateY: -2,
  },
  // Regeneration state. Used by Regenerable. Children fade to dimmedOpacity
  // over fadeMs; changed keys glow for glowHold then dissolve over glowDissolve.
  regen: {
    fadeMs: 200,
    dimmedOpacity: 0.4,
    glowHold: 400,
    glowDissolve: 200,
  },
  // Route change crossfade. Only the main content fades; sidebar stays crisp.
  navigation: {
    crossfadeDuration: 180,
    crossfadeEase: 'ease-out',
  },
  // WI-UI-CLEANUP-001 Stage 1 — sidebar hover-intent + width transition.
  // Enter delay prevents flyouts on accidental mouseovers; leave delay prevents
  // flicker when the cursor briefly exits (e.g. crossing a nav row border).
  // Label fade-in is staggered inside the width transition so labels don't
  // horizontally shift during expand.
  sidebar: {
    widthDurationMs:   180,
    widthEase:         'cubic-bezier(0.4, 0, 0.2, 1)',
    hoverEnterDelayMs: 120,
    hoverLeaveDelayMs: 200,
    labelFadeDelayMs:  120,
    labelFadeDurationMs: 120,
  },
  // Skeleton shimmer on first-mount loading.
  loading: {
    shimmerDuration: 1600,
  },
  // Toast slide-in for success confirmations.
  toast: {
    slideDuration: 240,
    dwellMs: 3500,
  },
};

// ============================================================
// AI REWRITING STATE (Law: VISUAL_UNIFORMITY)
// Single authority for all AI rewrite visual states across the
// entire system — blur, ring, pulse animation, labels.
// ============================================================

export const AI_REWRITING_STATE = {
    ringColor:        DS_COLORS.ai.action,
    blurAmount:       '8px',
    dimOpacity:       0.25,
    spinnerIcon:      'SpinnerGap',
    spinnerSize:      20,
    pulseAnimation:   'aiRewritePulse 1.4s ease-in-out infinite',
    buttonLoadingLabel: '{section} Rewriting\u2026',
    ringWidth:        '2px',
    ringStyle:        'solid',
    transition:       'all 200ms ease',
    pulseKeyframes:   '@keyframes aiRewritePulse { 0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(109,40,217,0.4); } 50% { opacity: 0.7; box-shadow: 0 0 0 6px rgba(109,40,217,0); } }',
};

// ============================================================
// LAYOUT SIZES (Law: LAYOUT_AUTHORITY)
// Sidebar width and structural layout dimensions.
// ============================================================

export const LAYOUT_SIZES = {
    sidebarWidth: 240,     // px — Atrium-style sidebar; matches DS_SPACING.sidebarWidth (DESIGN_IMPLEMENTATION_CONTRACT §2.2)
};

// ============================================================
// STATE EPOCH (Law: EPOCH_AUTHORITY)
// Bump this when localStorage schema changes. On mismatch,
// stale keys are purged automatically at boot.
// V3: bumped from 2 → 3 for SPLASH_DURATION_MS change and
// introduction of WELCOME_TIMING default UI state.
// V4: bumped from 3 → 4 for brand navy alignment to #191970 per
// brand_registry.json (WI-DESIGN-NAVY-001).
// V5: bumped from 4 → 5 — collapsible section key shape changed
// from agents/data to office/sourcing/relationships/content/
// analysis/reference (WI-DESIGN-IMPL-001 Phase 2 Sidebar).
// Stale localStorage keys from V4 (accrezeo_sidebar_collapsed
// storing old group IDs) are purged by UI_SESSION.wipeStaleState
// at boot when epoch mismatch is detected.
// V6: bumped from 5 → 6 — sidebar shape changed from always-expanded
// (collapsible sections) to narrow-default + hover-expand + pinnable
// (WI-UI-CLEANUP-001 Stage 1). The old accrezeo_sidebar_collapsed
// key no longer applies; the new accrezeo_sidebar_pinned key replaces
// per-section collapse state with a single rail-level pin boolean.
// V7: bumped from 6 → 7 — Deal Screen tiles now render BAND WORD only
// (no numeric band_score to reader); four-tile rationale added; reading
// order changed (context before verdict). WI-DS-RENDER-NAME-001.
// V8: bumped from 7 → 8 — DS_DECISION_LAYER token block added for the
// Gate C 4-row decision layer redesign (amberAccentBar, greenBadge,
// tileGrid, contrarianBox, carryChainFailBanner, typography tokens).
// Any consumer that stores decision-layer UI state must re-hydrate.
// V9: bumped from 8 → 9 — Post Studio's DEFAULT ACTIVE TAB changed. The whole
// idea-and-draft flow now lives on the agent page as tabs in the order the work
// moves (IDEA, PENDING, SAVED, POSTED, REJECTED), so the tab a fresh visit opens
// on is 'idea' and no longer 'pending'. The standalone /ideas route and its
// Idea Bank module-registry entry were removed in the same change, so any stored
// nav or tab state naming them is stale and must be purged.
// WI-IDEA-CLAIM-REPAIR-AND-ONE-DOOR-AGENT-PAGE-001 (CEO ruling 2026-07-30).
// ============================================================

export const STATE_EPOCH = 9;

// ============================================================
// RUNTIME STATE MANAGEMENT (Law: STATE_AUTHORITY)
// ============================================================

// Global phase state
let currentPhase = 'PRE_RENDER';

export const UI_PHASES = {
    PRE_RENDER: 'PRE_RENDER',
    BOOTSTRAP: 'BOOTSTRAP',
    RENDER: 'RENDER',
};

export const UI_STATE = {
    getPhase: () => currentPhase,
    setPhase: (phase) => {
        console.log(`[UI_STATE] Phase: ${currentPhase} → ${phase}`);
        currentPhase = phase;
    },
    shouldBlock: () => currentPhase !== UI_PHASES.RENDER,
};

// ============================================================
// SESSION MANAGEMENT (Law: SESSION_LIFECYCLE)
// ============================================================

const SESSION_STORAGE_KEY = 'accrezeo_session';

// Sidebar collapse localStorage key — purged on epoch bump (legacy V5 key)
const SIDEBAR_COLLAPSE_KEY = 'accrezeo_sidebar_collapsed';
// Sidebar pin localStorage key — rail-level pin state (WI-UI-CLEANUP-001 Stage 1)
export const SIDEBAR_PINNED_KEY = 'accrezeo_sidebar_pinned';
// Epoch tracking key — persisted to detect version bumps
const EPOCH_STORAGE_KEY = 'accrezeo_state_epoch';

export const UI_SESSION = {
    wipeStaleState: () => {
        // Clear stale session data on app init
        // CRITICAL: Page reload = new session, reset isFirstLoad
        localStorage.removeItem('accrezeo_welcomed');

        // STATE_EPOCH gate: purge sidebar collapse state when epoch changes.
        // V5 changed collapsible key shape from agents/data to
        // office/sourcing/relationships/content/analysis/reference.
        // Old keys stored in accrezeo_sidebar_collapsed are invalid.
        try {
            const storedEpoch = parseInt(localStorage.getItem(EPOCH_STORAGE_KEY) || '0', 10);
            if (storedEpoch !== STATE_EPOCH) {
                localStorage.removeItem(SIDEBAR_COLLAPSE_KEY);
                localStorage.removeItem(SIDEBAR_PINNED_KEY);
                localStorage.setItem(EPOCH_STORAGE_KEY, String(STATE_EPOCH));
            }
        } catch {
            localStorage.removeItem(SIDEBAR_COLLAPSE_KEY);
            localStorage.removeItem(SIDEBAR_PINNED_KEY);
        }

        const stored = localStorage.getItem(SESSION_STORAGE_KEY);
        if (stored) {
            try {
                // Always reset to clean slate on page reload
                localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
                    isFirstLoad: true,  // Reset: page reload = new first load
                    lifecycleComplete: false,  // Reset: needs new lifecycle
                }));
            } catch {
                localStorage.removeItem(SESSION_STORAGE_KEY);
            }
        } else {
            // No stored data, initialize clean
            localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
                isFirstLoad: true,
                lifecycleComplete: false,
            }));
        }
    },

    getIsLifecycleComplete: () => {
        try {
            const data = JSON.parse(localStorage.getItem(SESSION_STORAGE_KEY) || '{}');
            return data.lifecycleComplete === true;
        } catch {
            return false;
        }
    },

    setLifecycleComplete: () => {
        try {
            const data = JSON.parse(localStorage.getItem(SESSION_STORAGE_KEY) || '{}');
            data.lifecycleComplete = true;
            data.isFirstLoad = false;
            localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data));

            // Emit global event for App.js
            window.dispatchEvent(new Event('ACCREZEO_LIFECYCLE_CHANGED'));
        } catch (e) {
            console.error('[UI_SESSION] Failed to set lifecycle complete:', e);
        }
    },

    getIsFirstLoad: () => {
        try {
            const data = JSON.parse(localStorage.getItem(SESSION_STORAGE_KEY) || '{}');
            return data.isFirstLoad !== false;  // Default true
        } catch {
            return true;
        }
    },
};

// ============================================================
// STORAGE UTILITIES (Law: STORAGE_AUTHORITY)
// ============================================================

export const UI_STORAGE = {
    getIdentity: () => {
        try {
            return JSON.parse(localStorage.getItem('system_identity') || 'null');
        } catch {
            return null;
        }
    },

    setIdentity: (identity) => {
        try {
            localStorage.setItem('system_identity', JSON.stringify(identity));
        } catch (e) {
            console.error('[UI_STORAGE] Failed to set identity:', e);
        }
    },

    clearAll: () => {
        localStorage.clear();
        sessionStorage.clear();
    },
};

// ============================================================
// NAVIGATION STATE (Law: NAVIGATION_AUTHORITY)
// ============================================================

export const UI_NAVIGATION = {
    getPersistedTab: (module) => {
        try {
            const key = `nav_tab_${module}`;
            return localStorage.getItem(key) || null;
        } catch {
            return null;
        }
    },

    setPersistedTab: (module, tab) => {
        try {
            const key = `nav_tab_${module}`;
            localStorage.setItem(key, tab);
        } catch (e) {
            console.error('[UI_NAVIGATION] Failed to persist tab:', e);
        }
    },
};

// ============================================================
// MODULE CONFIGURATION (Law: MODULE_REGISTRY)
// ============================================================

export const UI_MODULES = {
    INVESTORS: {
        id: 'investors',
        label: 'Investors',
        TABS: [
            { id: 'all', label: 'All Investors' },
            { id: 'active', label: 'Active Pipeline' },
            { id: 'archived', label: 'Archived' }
        ]
    },
    STARTUPS: {
        id: 'startups',
        label: 'Startups',
        TABS: [
            { id: 'all', label: 'All Startups' },
            { id: 'portfolio', label: 'Portfolio' },
            { id: 'evaluate', label: 'Under Eval' }
        ]
    },
    KB: {
        id: 'knowledge-base',
        label: 'Knowledge Base',
        TABS: [
            { id: 'all', label: 'All entries' },
            { id: 'drafts', label: 'Drafts' },
            { id: 'published', label: 'Published' }
        ]
    },
    FOUNDERS: {
        id: 'founders',
        label: 'Founders',
        TABS: [
            { id: 'active', label: 'Active' },
            { id: 'bounced', label: 'Bounced' },
            { id: 'unassigned', label: 'Unassigned' }
        ]
    },
};

// ============================================================
// RESOLVER FUNCTIONS (Law: DATA_RESOLUTION)
// ============================================================

export const UI_RESOLVERS = {
    resolveModuleCount: (module) => {
        // Placeholder - will be populated with actual data fetching
        console.warn('[UI_RESOLVERS] resolveModuleCount not fully implemented');
        return 0;
    },

    resolveKBStatus: () => {
        // Placeholder - will be populated with actual status logic
        console.warn('[UI_RESOLVERS] resolveKBStatus not fully implemented');
        return 'unknown';
    },
};

// ============================================================
// BUTTON STATE CONTRACT (Law: Button State Enforcement)
// ============================================================

export const UI_BUTTON_STATE = {
    // Visual feedback for disabled state
    DISABLED: {
        opacity: '0.5',  // Matches ui/button.jsx disabled:opacity-50
        cursor: 'not-allowed',
        pointerEvents: 'none',
    },

    // Visual feedback for loading/async state
    LOADING: {
        opacity: '0.7',
        cursor: 'wait',
        pointerEvents: 'none',  // Block clicks during loading
    },

    // Transition timing for state changes
    TRANSITION_DURATION: '150ms',
};

// ============================================================
// CARD CONTRACT (Law: Card Size Enforcement)
// ============================================================

export const UI_CARD = {
    // Canonical card size
    PADDING: '1.5rem',  // 24px (p-6 in Tailwind)
    BORDER_RADIUS: '0.5rem',  // 8px (rounded-lg)

    // Shadow/elevation
    SHADOW: 'var(--shadow-linear)',  // Matches brand design
    SHADOW_HOVER: 'var(--shadow-md)',  // Optional hover elevation

    // Minimum height for consistency
    MIN_HEIGHT: '200px',  // Existing DIMENSIONS.CARD_MIN_HEIGHT

    // Background (references color contract)
    BACKGROUND: 'var(--bg-card)',  // Uses global color system

    // Border (references color contract)
    BORDER: '1px solid var(--border-card)',  // Uses global color system
};

// ============================================================
// DETAIL VIEW CONTRACT (Law: Detail View System-Sealed)
// ============================================================

export const UI_DETAIL_VIEW = {
    // Layout: Sheet pattern (sliding panel)
    SHEET_WIDTH: '800px',  // Per READ_VIEW_LAW.md (w-[800px])

    // Component authority
    REQUIRED_COMPONENT: 'StandardReadView',  // ONLY allowed detail view component

    // Behavior
    ESCAPE_CLOSES: true,  // NAV-001: Escape key must close
    PRESERVES_CONTEXT: true,  // Sheet maintains parent list context

    // Structure (schema-driven)
    REQUIRES_SCHEMA: true,  // Must render structured sections (not single blob)
    NO_NESTED_CARDS: true,  // Detail view IS the card (no cards inside cards)

    // Actions (inherited from Button contract)
    ACTIONS_USE_BUTTON_STATE: true,  // Accept, Reject, Send, Close use BUTTON_STATE
};

// ============================================================
// AUTHORITY & VALIDATION (Law: CONTRACT_ENFORCEMENT)
// ============================================================

export const UI_AUTHORITY = {
    enforceVisualLaw: () => {
        // Validates that visual constants are being used
        console.log('[UI_AUTHORITY] Visual law enforcement enabled');
    },

    detectAuthorityMismatch: () => {
        // Detects if multiple authority sources conflict
        console.warn('[UI_AUTHORITY] detectAuthorityMismatch not fully implemented');
        return false;
    },
};

// Contract validation — V3: validate against DESIGN_INTENTS intent keys, not raw hex.
export function validateButtonColor(intent) {
    const validIntents = Object.keys(DESIGN_INTENTS);
    if (!validIntents.includes(intent)) {
        console.warn('[UI_CONTRACT_VIOLATION] Button intent not from DESIGN_INTENTS:', intent);
        return false;
    }
    return true;
}

// ============================================================
// DS_DECISION_LAYER — Gate C 4-row decision layer design tokens
//
// Authority: task-display-config.js (label copy) + ui-invariants.js (this block, visual tokens).
// Brand hex authority: backend/config/brand_registry.json (this file must not contradict it).
//
// amberAccentBar: #B45309 (amber-700 class). Contrast ratio vs white ≈ 5.7:1 (passes ≥4.5:1
//   WCAG AA). Distinct from brand verdict_weak (#D97706 / amber-600) — used exclusively for
//   the decision-layer accent bar on weak/no-signal tiles.  NOT used for verdict pills
//   (those continue to use DS_COLORS.signal.moderate / #D97706).
// greenBadge: matched to brand verdict_strong (#059669 / emerald-600 per brand_registry) for
//   the "Worth a Call" badge background/text, consistent with VERDICT_PILL.strong.
// All interactive targets: minHeight 44px per DS_SPACING.minTouchTarget.
// Typography: DM Sans for structural labels (h1); Inter for prose (openingLine).
// ============================================================

export const DS_DECISION_LAYER = {
  // Amber accent bar for tiles whose band is WEAK or NO_SIGNAL.
  // #B45309 = amber-700 class. Contrast vs white ≈ 5.7:1 (WCAG AA).
  // Kept separate from DS_COLORS.signal.moderate (#D97706) to avoid semantic collision.
  amberAccentBar: '#B45309',

  // "Worth a Call" recommendation badge.
  // greenBadgeBg uses brand verdict_strong_bg (#ECFDF5) from brand_registry.
  // greenBadgeText uses brand verdict_strong (#059669) from brand_registry.
  greenBadgeBg:   '#ECFDF5',
  greenBadgeText: '#059669',

  // 4-up tile grid — responsive, 2-col on mobile, 4-col on >=lg.
  // minTileHeight enforces the 44px touch-target rule on each tile.
  tileGrid: {
    columns:       'repeat(auto-fit, minmax(160px, 1fr))',
    gap:           '12px',
    minTileHeight: '44px',
  },

  // Contrarian (counter-case) box — bordered, not muted.
  // Uses border_default from brand_registry, not a tinted background, so it
  // reads as distinct from the muted rationale panel.
  contrarianBox: {
    background:   '#FFFFFF',
    border:       '2px solid #E5E7EB',   // DS_COLORS.border.default
    borderRadius: '8px',                 // DS_BORDERS.radiusCard
    padding:      '16px 20px',
    minHeight:    '44px',
  },

  // Carry-chain fail banner — amber background, dark text.
  // Background is brand verdict_weak_bg (#FFFBEB) for amber family consistency.
  // Text is brand text_primary (#111827) for maximum contrast on the light amber bg.
  // Left accent uses amberAccentBar (#B45309) to match the tile accent colour.
  carryChainFailBanner: {
    background:   '#FFFBEB',             // brand_registry verdict_weak_bg
    borderLeft:   '4px solid #B45309',  // amberAccentBar — dark amber, >=4.5:1
    borderOther:  '1px solid #FCD34D',  // amber-300 subtle border
    borderRadius: '8px',
    padding:      '12px 16px',
    textColor:    '#111827',            // brand text_primary — strong contrast on #FFFBEB
    minHeight:    '44px',
  },

  // h1 — top-level recommendation heading.
  // DM Sans 28px / 800 (structural font, matching DS_TYPOGRAPHY.pageTitle spec).
  h1: {
    fontFamily:    'DM Sans, system-ui, sans-serif',
    fontSize:      '28px',
    fontWeight:    800,
    lineHeight:    1.15,
    letterSpacing: '-0.025em',
    color:         '#111827',           // brand text_primary
  },

  // openingLine — recommendation prose beneath the h1.
  // Inter 16px / 400 (prose font, matching DS_TYPOGRAPHY.bodyMd).
  openingLine: {
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize:   '16px',
    fontWeight:  400,
    lineHeight:  1.6,
    color:      '#111827',              // brand text_primary
  },

  // questionBox — single question for the call.
  // Brand-navy-light background, navy left border, Inter body prose.
  questionBox: {
    background:   '#F0F0F8',            // brand_registry brand_navy_light_bg
    borderLeft:   '3px solid #191970', // brand_registry brand_navy
    borderRadius: '6px',
    padding:      '12px 16px',
    minHeight:    '44px',
    fontFamily:   'Inter, system-ui, sans-serif',
    fontSize:     '16px',
    fontWeight:   400,
    lineHeight:   1.6,
    color:        '#111827',            // brand text_primary
  },
};

// ============================================================
// STYLE_GATE_NOTICE — WI-DS-STYLE-GATE-UI-SURFACE-001
// Visual tokens for the StyleGateNotice banner (role="alert").
//
// Precedent: DS_DECISION_LAYER.carryChainFailBanner (amber caution pattern).
// Background: #FFF7ED (orange-50) — warm amber family, strong contrast for dark text.
// borderLeft: 4px solid #B45309 (amber-700). Contrast vs white ≈ 5.7:1 (WCAG AA).
// headingColor / bodyColor / listColor: #111827 (text_primary). Contrast on #FFF7ED ≈ 16:1.
// All min-height buttons: 44px per DS_SPACING.minTouchTarget.
// Zero hardcoded hex outside this block. Authority: backend/config/brand_registry.json.
// ============================================================

export const STYLE_GATE_NOTICE = {
  // Banner shell
  background:   '#FFF7ED',             // orange-50 — warm amber family, strong contrast
  borderLeft:   '4px solid #B45309',  // amber-700 — same dark amber used in carryChainFailBanner
  borderOther:  '1px solid #FDE68A',  // amber-200 subtle outer border
  borderRadius: '8px',                // DS_BORDERS.radiusCard
  padding:      '20px 24px',

  // Typography colors — all #111827 (text_primary), ≥16:1 contrast on #FFF7ED
  headingColor: '#111827',            // brand text_primary
  bodyColor:    '#111827',            // brand text_primary
  listColor:    '#111827',            // brand text_primary

  // Heading (≥20px bold per accessibility law)
  headingFontSize:   '20px',
  headingFontWeight: 700,
  headingFontFamily: 'DM Sans, system-ui, sans-serif',

  // Body / subtext (≥16px per accessibility law)
  bodyFontSize:   '16px',
  bodyFontWeight: 400,
  bodyFontFamily: 'Inter, system-ui, sans-serif',
  bodyLineHeight: 1.6,

  // Re-run button — minHeight 44px (touch target law)
  buttonMinHeight: '44px',
  buttonPadding:   '10px 24px',
};

// ============================================================
// AGGREGATE EXPORT (Law: SINGLE_IMPORT_PATTERN)
// ============================================================

export const UI_INVARIANTS = {
    COLORS: UI_COLORS,
    DESIGN_INTENTS,
    SEMANTIC_COLORS,
    DIMENSIONS: UI_DIMENSIONS,
    TYPOGRAPHY: UI_TYPOGRAPHY,
    TIMINGS: UI_TIMINGS,
    WELCOME_TIMING,
    WELCOME_SIZES,
    LAYOUT_SIZES,
    STATE_EPOCH,
    PHASES: UI_PHASES,
    STATE: UI_STATE,
    SESSION: UI_SESSION,
    STORAGE: UI_STORAGE,
    NAVIGATION: UI_NAVIGATION,
    MODULES: UI_MODULES,
    RESOLVERS: UI_RESOLVERS,
    AUTHORITY: UI_AUTHORITY,
    BUTTON_STATE: UI_BUTTON_STATE,  // Button State Enforcement
    CARD: UI_CARD,  // Card Size Enforcement
    DETAIL_VIEW: UI_DETAIL_VIEW,  // Detail View System-Sealed
    DEAL_SCREEN_CARD,  // DS-CARD-V4 prototype tokens
    VERDICT_PILL,      // DS-CARD-V4 verdict pill config
    CALL_BADGE,        // DS-CARD-V4 call decision badge config
    DS_DETAIL_VIEW,    // DS-CARD-V4 detail view tokens
    DS_TYPOGRAPHY,     // DS-CARD-V5 typography tokens
    DS_COLORS,         // DS-CARD-V5 semantic colors
    KPI_TILE,          // DS-CARD-V5 KPI tile tokens
    SECTION_PANEL,     // DS-CARD-V5 section panel tokens
    DECISION_RATIONALE, // DS-CARD-V5 rationale panel tokens
    RATIONALE_BLOCK,   // DS-CARD-V5 rationale block tokens (per-section Q&A)
    ACTION_BUTTONS,    // DS-CARD-V5 action button tokens
    DS_SPACING,        // DESIGN_LANGUAGE_SKILL.md spacing system
    DS_BORDERS,        // DESIGN_LANGUAGE_SKILL.md border and shape
    DS_ANIMATION,      // WI-PROGRESS-CARD-POC animation tokens
    DS_MOTION,         // DESIGN_IMPLEMENTATION_CONTRACT.md Section 4 motion vocabulary
    BAND_WORD_ALWAYS_VISIBLE, // WI-DS-RENDER-NAME-001 band-word rendering invariant
    DS_DECISION_LAYER,        // Gate C 4-row decision layer design tokens
    STYLE_GATE_NOTICE,        // WI-DS-STYLE-GATE-UI-SURFACE-001 style gate banner tokens
    validateButtonColor,
};

// Default export
export default UI_INVARIANTS;
